/* eslint-disable no-underscore-dangle */
/*
  Forward Transformation (Backend → Redux)

  This module handles the forward transformation from backend API responses
  to the normalized document model stored in Redux. It includes data normalization,
  field completion, and EOJ reconciliation handling.
*/

import type {
  ApiJourneyVersionData,
  JourneyDocument,
  ReconciliationConditions as ApiReconciliationConditions,
} from '../types';
import type { ReconciliationConditions as DocReconciliationConditions } from '../types/document';
import type { InvestigationType } from '../types/shared';
import { mapApiNodeToDocNode } from './transform/nodeMapping';
import { mapApiEdgeToDocEdge } from './transform/edgeMapping';
import { handleEojReconciliation } from './transform/reconciliation';

/**
 * Convert reconciliation conditions from API format (camelCase) to document format (snake_case)
 */
function convertReconciliationConditions(
  apiConditions: ApiReconciliationConditions,
): DocReconciliationConditions {
  return apiConditions?.map(rule => ({
    id: rule.id,
    operator: rule.operator,
    conditions: rule.conditions.map(condition => ({
      id: condition.id,
      quantity: condition.quantity,
      branch: condition.branch,
      entity_application_outcome: condition.entityApplicationOutcome,
    })),
    journey_application_outcome: rule.journeyApplicationOutcome,
  })) ?? [];
}

/**
 * Forward transformation: Backend API Response → Redux Document Model
 *
 * This function orchestrates the complete transformation from backend API format
 * to the normalized document model. It performs:
 * 1. Node conversion and normalization
 * 2. Edge conversion and normalization
 * 3. EOJ reconciliation migration
 * 4. Edge precedence auto-assignment
 * 5. Metadata preservation
 *
 * @param api - The API response data
 * @param options - Optional configuration (e.g., investigation types)
 * @returns The normalized journey document
 */
export function forwardTransform(
  api: ApiJourneyVersionData,
  options?: { investigationTypes?: InvestigationType[] },
): JourneyDocument {
  // Transform nodes and edges
  const nodesArr = api.nodes.map(mapApiNodeToDocNode);
  const edgesArr = api.edges.map((edge, idx) => mapApiEdgeToDocEdge(
    edge,
    idx,
    options?.investigationTypes,
  ));

  // Convert to keyed objects
  const nodes = Object.fromEntries(nodesArr.map(n => [n.id, n]));
  const edges = Object.fromEntries(edgesArr.map(e => [e.id, e]));

  // Extract journey token and name from _embedded data if present
  const journeyToken = api._embedded?.journey?.journey_token;
  const journeyName = api._embedded?.journey?.name;
  const journeyType = api._embedded?.journey?.type;

  let doc: JourneyDocument = {
    nodes,
    edges,
    start_node_id: api.start_node_id,
    terminal_reconciliation_workflow_node_id: api.terminal_reconciliation_workflow_node_id ?? null,
    // Preserve metadata when present
    ...(api.active !== undefined && { active: api.active }),
    ...(api.activated_at !== undefined && { activated_at: api.activated_at }),
    ...(api.agent_name !== undefined && { agent_name: api.agent_name }),
    ...(api.archived !== undefined && { archived: api.archived }),
    ...(api.change_log !== undefined && { change_log: api.change_log }),
    ...(api.created_at !== undefined && { created_at: api.created_at }),
    ...(api.notes !== undefined && { notes: api.notes }),
    // Map version_id from API to version in document
    ...(api.version_id !== undefined && { version: api.version_id }),
    ...(api.parent_version_id !== undefined && { parent_version_id: api.parent_version_id }),
    ...(journeyToken !== undefined && { token: journeyToken }),
    ...(journeyName !== undefined && { name: journeyName }),
    ...(journeyType !== undefined && { type: journeyType }),
    ...(api.is_multi_entity !== undefined && { is_multi_entity: api.is_multi_entity }),
    ...(api.reconciliation_conditions !== undefined && {
      reconciliation_conditions: convertReconciliationConditions(api.reconciliation_conditions),
    }),
  };

  // Handle EOJ reconciliation migration and flag propagation
  doc = handleEojReconciliation(doc);

  // Auto-assign edge precedence - @todo disable for now
  // doc = assignEdgePrecedence(doc);

  return doc;
}
